import { ClassificacaoItem } from './common.types';
import type { ResumoDocumentoNotificacao } from './documentoNotificacao.types';
import { EnvioDigitalOutput, EnvioFisicoOutput } from './envio.types';
import { ManifestacaoOutput } from './manifestacao.types';
import { StatusCorreios, StatusEmail, StatusNotificacao, StatusWhatsApp } from './status.types';

export { StatusNotificacao };

export interface ContatoData {
  emails: string[];
  telefones: string[];
  nomeContato?: string;
  nome?: string;
  cpfCnpj?: string;
}

// TODO(Arbitralis-dev/arbitralis-core-api#921): remover 'LIMITE_EXCEDIDO' quando não houver mais itens com esse motivo em metadata._errosContato — o backend não o produz mais desde a remoção do teto de contatos.
export type ContatoErrorMotivo = 'FORMATO_INVALIDO' | 'LIMITE_EXCEDIDO';

export interface ContatoParseError {
  campo: 'email' | 'telefone';
  indice: number;
  valor: string;
  motivo: ContatoErrorMotivo;
}

export interface EnderecoData {
  cep: string;
  logradouro: string;
  numero: string;
  complemento?: string;
  bairro: string;
  cidade: string;
  uf: string;
  referencia?: string;
}

export interface ParteEndereco {
  id: string;
  cep: string;
  logradouro: string;
  numero: string;
  complemento?: string | null;
  bairro: string;
  cidade: string;
  estado: string;
  principal?: boolean;
}

export interface PartePessoaEmail {
  email: string;
  principal?: boolean;
}

export interface PartePessoaTelefone {
  numeroNormalizado?: string | null;
  numeroFormatado?: string | null;
  principal?: boolean;
}

export interface PartePessoa {
  id: string;
  nomeCompleto: string;
  cpfCnpj?: string | null;
  cpfCnpjFormatado?: string | null;
  email?: string | null;
  telefone?: string | null;
  emails?: PartePessoaEmail[];
  telefones?: PartePessoaTelefone[];
}

export interface ParteItem {
  id: string;
  pessoaId: string;
  tipoParte: 'REQUERENTE' | 'REQUERIDO' | string;
  partePrincipal: boolean;
  ordemExibicao: number;
  pessoa?: PartePessoa;
  enderecos?: ParteEndereco[];
}

export interface ParteExtrajudicialItem {
  id: string;
  participacoes: Array<{ papel: 'CREDOR' | 'DEVEDOR' | string }>;
  parte: {
    id?: string;
    pessoaId?: string;
    pessoa?: PartePessoa;
    emails?: PartePessoaEmail[];
    telefones?: PartePessoaTelefone[];
    enderecos?: ParteEndereco[];
  };
}

export interface RespostaNotificacao {
  id: string;
  texto: string;
  anexo?: {
    nomeArquivo: string;
    urlDownload: string;
    tamanho: number;
    tipoArquivo: string;
  };
  dataResposta: string;
  lida: boolean;
  arquivada: boolean;
}

export interface ItemOutput {
  id: string;
  empresaId: string;
  escritorioId: string;
  loteId: string;
  numeroNotificacao: string | null;
  linhaNumero: number | null;
  statusGeral?: StatusNotificacao;
  statusLabel?: string;
  breveRelato: string;
  valor: number;
  prazoDias?: number | null;
  metadata?: Record<string, string> | null;
  classificacao: ClassificacaoItem;
  erros: string[];
  warnings?: string[];
  revisaoMotivos?: string[];
  errosPorCampo?: Record<string, string>;
  errosContato?: ContatoParseError[];
  nomeDivergente?: boolean;
  partes?: ParteItem[];
  partesExtrajudiciais?: ParteExtrajudicialItem[];
  dataCriacao: string;
  dataAtualizacao: string;
  canceledAt?: string | null;
}

export interface ItemFilters {
  loteId?: string;
  classificacao?: ClassificacaoItem;
  statusGeral?: string;
  search?: string;
  valorMin?: number;
  valorMax?: number;
  page?: number;
  pageSize?: number;
  sortBy?: 'dataCriacao' | 'valor' | 'classificacao' | 'linhaNumero';
  sortOrder?: 'asc' | 'desc';
}

export interface ItemWithRelations extends ItemOutput {
  documentoNotificacao?: ResumoDocumentoNotificacao | null;
  lote: {
    id: string;
    batchName: string;
    batchCode: string;
    sendMethod?: string;
    sendAfter?: string | null;
  };
  allowedActions?: AllowedAction[];
  custoEstimadoCorreio?: string | null;
  enviosDigitais?: EnvioDigitalOutput[];
  envioFisico?: EnvioFisicoOutput | null;
  manifestacoes?: ManifestacaoOutput[];
  resposta?: RespostaNotificacao;
  negociacaoCasos?: Array<{ id: string; status: string }>;
}

export type AllowedAction =
  | 'view_details'
  | 'edit'
  | 'cancel'
  | 'send_now'
  | 'edit_schedule'
  | 'resend_digital'
  | 'archive'
  | 'restore'
  | 'open_negotiation'
  | 'view_negotiation'
  | 'download_certificate'
  | 'retry';

/** Coluna de data por que a lista filtra — espelha `campoData` do core. */
export type NotificacaoPeriodoCampo = 'criacao' | 'envio' | 'visualizacao';

export interface NotificacaoListItem {
  id: string;
  numeroNotificacao: string | null;
  status: StatusNotificacao;
  statusLabel: string;
  sendMethod: string | null;
  sendMethodLabel: string | null;
  classificacao: string;
  dataCriacao: string;
  dataEnvio: string | null;
  dataVisualizacao: string | null;
  dataAtualizacao: string;
  lastEventAt: string | null;
  valor: number | null;

  notificado: {
    nome: string | null;
    cpfCnpj: string | null;
    email: string | null;
    telefone: string | null;
  };

  extraNotifiedCount: number;
  hasNegotiation: boolean;
  negotiationStatus: string | null;
  temManifestacao: boolean;
  manifestacaoLida: boolean;
  custoEstimadoCorreio: string | null;

  enviosDigitais: Array<{
    canal: string;
    statusEmail: StatusEmail | null;
    statusWhatsApp: StatusWhatsApp | null;
    destinatario?: string | null;
    dataEnvio: string | null;
  }>;

  envioFisico: {
    statusCorreios: StatusCorreios;
    codigoRastreio: string | null;
    custoEnvio: number | null;
    scrybeCanCancelUntil?: string | null;
  } | null;

  warnings: string[];
  revisaoMotivos: string[];

  lote: {
    id: string;
    batchName: string;
    batchCode: string;
    sendMethod: string | null;
    sendAfter?: string | null;
  };

  allowedActions: AllowedAction[];
}

export interface NotificacaoFilters {
  loteId?: string;
  classificacao?: ClassificacaoItem;
  status?: StatusNotificacao | string;
  sendMethod?: string;
  search?: string;
  campoData?: NotificacaoPeriodoCampo;
  dataInicio?: string;
  dataFim?: string;
  hasNegotiation?: boolean;
  onlyDelivered?: boolean;
  onlySent?: boolean;
  onlyWarning?: boolean;
  onlyFailed?: boolean;
  onlyArchived?: boolean;
  hasManifestation?: boolean;
  page?: number;
  pageSize?: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

export interface SendDigitalInput {
  metodo: 'EMAIL' | 'WHATSAPP' | 'EMAIL_WHATSAPP';
  whatsappTemplateId?: string;
  emailTemplateId?: string;
  destinatario?: {
    email?: string;
    telefone?: string;
  };
}

export interface SendFisicoInput {
  fisicoTemplateId?: string;
  destinatario?: {
    nome?: string;
    endereco?: EnderecoData;
  };
}

export interface CustomTemplateVariable {
  key: string;
  descricao?: string | null;
  exemplo?: string | null;
}

export interface ItemEditData {
  id: string;
  linhaNumero: number;
  nome: string | null;
  documento: string | null;
  contato: ContatoData | null;
  endereco: EnderecoData | null;
  sendMethod: string;
  classificacao: ClassificacaoItem;
  errors: Record<string, string>;
  canSave: boolean;
  breveRelato: string | null;
  valor: number | null;
  margemNegociacao: number | null;
  partes?: ParteItem[];
  metadata?: Record<string, string>;
  customVariables?: CustomTemplateVariable[];
}

export interface ItemUpdateInput {
  nome?: string;
  documento?: string;
  contato?: ContatoData;
  endereco?: EnderecoData;
  breveRelato?: string;
  valor?: number;
  margemNegociacao?: number;
  metadata?: Record<string, string>;
  prazoDias?: number | null;
}

export interface FieldError {
  field: string;
  message: string;
}

export interface ItemUpdateResult {
  success: boolean;
  item?: ItemWithRelations;
  errors?: Record<string, string>;
  fieldErrors?: FieldError[];
  classificacao?: ClassificacaoItem;
}

export interface ItemIndividualInput {
  breveRelato: string;
  valor: number;
  contato?: ContatoData;
  endereco?: EnderecoData;
  margemNegociacao?: number;
  requerentePessoaId?: string;
  requeridoPessoaId?: string;
}
